import type BackOfficeConnector from "../data/backOfficeConnector";
import type { IPagination } from "../data/types";
import type ChargesOrderDifferences from "../entities/chargesOrderDifferences";
import Distributor from "../entities/distributor";
import DistributorIssuedPaymentRequestResponse from "../entities/distributorIssuedPaymentRequestResponse";
import DistributorPaymentRequestResponse from "../entities/distributorPaymentRequestResponse";
import type DistributorStorno from "../entities/distributorStorno";
import type SearchDistributorsIssuedPaymentRequestsRequest from "../entities/searchDistributorsIssuedPaymentRequestsRequest";
import type EventBus from "../services/eventBus";
import { serializeFilters } from "../utils/filters";
import RepositoryBase from "./repositoryBase";
import axios from "axios";
import SalespointResponse from "../entities/salespointResponse";

export default class DistributorRepository extends RepositoryBase<BackOfficeConnector> {
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  distributorStorno(distributorStorno: DistributorStorno) {
    const data = new FormData();
    data.set("cancellation_record_id", distributorStorno.cancellationRecordId);
    if (distributorStorno.file) {
      data.set("file", distributorStorno.file);
    }

    return this.callApi(
      api =>
        api
          .all("call_center")
          .one("distributors", distributorStorno.distributorId)
          .all("charge_order_cancellations")
          .postData(data),
      { useGenericErrorMessageHandler: false }
    );
  }

  chargesOrderDifferences(chargesOrderDifferences: ChargesOrderDifferences) {
    const data = new FormData();
    if (chargesOrderDifferences.file) {
      data.set("file", chargesOrderDifferences.file);
    }

    return this.callApi(
      api =>
        api
          .all("call_center")
          .one("distributors", chargesOrderDifferences.distributorId)
          .one("payment_requests", chargesOrderDifferences.paymentRequestId)
          .all("charges_order_differences")
          .postData(data),
      { useGenericErrorMessageHandler: false, ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }) }
    );
  }

  distributorPaymentRequestsSimpleList({ distributorId }: { distributorId: string }) {
    return this.callApi(api =>
      api
        .all("call_center")
        .all("simple_lists")
        .one("distributors", distributorId)
        .all("payment_requests")
        .getEntities(DistributorPaymentRequestResponse)
    );
  }

  distributorPaymentRequests(
    distributorId: string,
    paging?: IPagination,
    filters?: SearchDistributorsIssuedPaymentRequestsRequest
  ) {
    return this.callApi(api =>
      api
        .all("call_center")
        .one("distributors", distributorId)
        .all("payment_request_search")
        .postPagedEntities({ ...serializeFilters(filters, false), ...paging }, DistributorIssuedPaymentRequestResponse)
    );
  }

  distributorPaymentRequestReports(distributorId: string, filters?: SearchDistributorsIssuedPaymentRequestsRequest) {
    return this.callApi(api =>
      api
        .all("call_center")
        .one("distributors", distributorId)
        .all("payment_request_reports")
        .getRaw({ ...serializeFilters(filters) })
    );
  }

  async distributorPaymentRequestReportDownload(distributorId: string, paymentId: string) {
    const { distributorPaymentRequestDetailLink } = await this.callApi(
      api =>
        api
          .all("call_center")
          .one("distributors", distributorId)
          .one("payment_request_reports", paymentId)
          .get<{ distributorPaymentRequestDetailLink: string }>(),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }),
      }
    );

    return axios.get(distributorPaymentRequestDetailLink, {
      responseType: "arraybuffer",
    });
  }

  get(userId: string) {
    return this.callApi(api => api.one("distributors", userId).getEntity<Distributor>(Distributor), {
      ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }),
    });
  }

  getAll() {
    return this.callApi(api =>
      api.all("call_center").all("simple_lists").all("distributors").getEntities<Distributor>(Distributor)
    );
  }

  salesPoints(search: string, limit?: number) {
    return this.callApi(api =>
      api
        .all("distributors")
        .all("simple_lists")
        .all("salespoints")
        .getEntities<SalespointResponse>(SalespointResponse, { search, limit })
    );
  }

  getAllActive() {
    return this.callApi(api => api.all("distributors").getEntities<Distributor>(Distributor), {
      ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }),
    });
  }

  create(distributor: Distributor) {
    return this.callApi(api => api.all("distributors").postEntity(distributor, Distributor), {
      ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }),
    });
  }

  update(distributor: Distributor) {
    return this.callApi(api => api.one("distributors", distributor.id).putEntity<Distributor>(distributor, Distributor), {
      ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }),
    });
  }

  delete(entity: Distributor) {
    return this.callApi(api => api.one("distributors", entity.id).delete(entity), {
      ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }),
    });
  }
}
